import { AfterContentChecked, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { Campo, FormComponent } from '@components/form/form.component';
import { MatDialogRef } from '@angular/material/dialog';
import { ContratoRepository } from '@data/contrato/contrato.repository';
import { ContratoGrupoRepository } from '@data/contrato-grupo/contrato-grupo.repository';
import { AtendimentoRepository } from '@data/atendimento/atendimento.repository';
import { MotivoAtendimentoRepository } from '@data/tipo-atendimento/motivo-atendimento.repository';
import { MotivoSinistroRepository } from '@data/tipo-sinistro/tipo-sinistro.repository';
import { MunicipioRepository } from '@data/municipio/municipio.repository';
import { AnexoRepository } from '@data/anexo/anexo.repository';
import { CepRepository } from '@data/cep/cep.repository';
import { FileData } from '@components/anexos/file/file.component';
import { modal } from '@shared/alerts/modal/modal.alert';
import { AuthService } from '@app/infra/auth/auth.service';
import { FornecedoresRepository } from '@app/data/fornecedores/fornecedores.repository';
import { CondutorRepository } from '@data/condutor/condutor.repository';
import { of } from 'rxjs';
import * as moment from 'moment';

type VeiculosDisponiveis = {
  label: string;
  value: string;
}[];

@Component({
  selector: 'app-abrir-atendimento',
  templateUrl: './abrir-atendimento.component.html',
  styleUrls: ['./abrir-atendimento.component.scss'],
})
export class AbrirAtendimentoComponent implements AfterContentChecked {
  @ViewChild('formVeiculo') formVeiculo: FormComponent | undefined = undefined;
  @ViewChild('formSinistro') formSinistro: FormComponent | undefined = undefined;
  @ViewChild('formCliente') formCliente: FormComponent | undefined = undefined;

  protected condutorId: string | undefined;
  protected veiculosDisponiveis: VeiculosDisponiveis = [];

  protected camposDadosVeiculo: Campo[] = [
    {
      name: 'motivoAtendimentoId',
      label: 'Tipo de atendimento',
      type: 'select',
      width: '40%',
      selectOptions: {
        filter: this.motivoAtendimentoRepository.filter.bind(this.motivoAtendimentoRepository),
      },
      validators: [Validators.required],
    },
    {
      name: 'registrarSinistro',
      label: 'Registrar sinistro',
      type: 'select',
      width: '27.5%',
      selectOptions: {
        items: [
          { value: 'S', label: 'Sim' },
          { value: 'N', label: 'Não' },
        ],
      },
      validators: [Validators.required],
    },
    {
      name: 'grupoContratoId',
      label: 'Grupo Contrato Master',
      type: 'search',
      width: '32.5%',
      searchOptions: {
        key: 'numeroGrupoContrato',
        initialSearch: true,
        format: (value: string) => '%' + value + '%',
        filter: this.contratoGrupoRepository.filter.bind(this.contratoGrupoRepository),
      },
      validators: [Validators.required],
      onChange: (value: string) => {
        const clienteId = this.authService.decodeToken?.clienteId ?? '';
        const search = `grupoContratoId=${value}&status.descricao=Aberto,Fechado&encerrado=N&clienteId=${clienteId}`;

        this.contratoRepository.filterVeiculos(search).subscribe({
          next: (veiculos: any) => {
            this.veiculosDisponiveis = veiculos;
            const veiculoCampo = this.camposDadosVeiculo.find(campo => campo.name === 'veiculoId');
            if (veiculoCampo){
              veiculoCampo.disabled = false;
            }

            const control = this.formVeiculo?.form.get('veiculoId');

            if (control) {
              control.enable();
            }

            const controlVeiculo = this.formVeiculo?.form.get('veiculoId');

            if (controlVeiculo) {
              controlVeiculo.enable();
              controlVeiculo.reset();
            }
          },
          error: (err) => {
            console.error("Erro ao carregar veículos:", err);
          }
        });
      },
    },
    {
      name: 'veiculoId',
      label: 'Veículo',
      type: 'search',
      width: '50%',
      disabled: true,
      searchOptions: {
        key: 'placa',
        initialSearch: true,
        filter: (value: string) => {
          if (!value) {
            return of(this.veiculosDisponiveis);
          }

          const term = decodeURIComponent((value ?? '').split('=')[1] || '').trim().toLowerCase();
          const filtered = this.veiculosDisponiveis.filter((v: any) =>
            (v.label || '').toLowerCase().includes(term)
          );
          return of(filtered);
        },
      },
      validators: [Validators.required],
    },
    {
      name: 'odometroAtual',
      label: 'Odômetro atual',
      type: 'text',
      mask: '0*',
      width: '50%',
      validators: [Validators.required],
    },
  ];

  protected camposDadosSinitro: Campo[] = [
    {
      name: 'motivoSinistroId',
      label: 'Tipo de sinistro',
      type: 'select',
      width: '60%',
      selectOptions: {
        filter: this.motivoSinistroRepository.filter.bind(this.motivoSinistroRepository),
      },
      validators: [Validators.required],
    },
    {
      name: 'dataAcidente',
      label: 'Data do acidente',
      type: 'date',
      width: '40%',
      validators: [Validators.required],
    },
    {
      name: 'horaAcidente',
      label: 'Hora do acidente',
      type: 'time',
      width: '40%',
      validators: [Validators.required],
    },
    {
      name: 'numeroBO',
      label: 'Nº do B.O',
      type: 'text',
      mask: '0*',
      width: '60%',
      validators: [Validators.required],
    },
    {
      name: 'houveVitima',
      label: 'Houve vítima',
      type: 'checkbox',
      width: (100 / 3) + '%',
    },
    {
      name: 'houveVitimaFatal',
      label: 'Houve vítima fatal',
      type: 'checkbox',
      width: (100 / 3) + '%',
    },
    {
      name: 'declaracaoCulpa',
      label: 'Declaração de culpa',
      type: 'checkbox',
      width: (100 / 3) + '%',
    },
    {
      name: 'descricaoOcorrido',
      label: 'Dinâmica do acidente',
      type: 'textarea',
      width: '100%',
      validators: [Validators.required],
    },
    {
      name: '',
      label: 'Dados do condutor',
      type: 'separator',
    },
    {
      name: 'condutorNome',
      label: 'Nome do condutor',
      type: 'text',
      width: '100%',
      validators: [Validators.required],
    },
    {
      name: 'condutorHabilitacao',
      label: 'Habilitação do condutor',
      type: 'text',
      width: '60%',
      mask: '0*',
      validators: [Validators.required],
    },
    {
      name: 'condutorOrgao',
      label: 'Órgão expedidor',
      type: 'text',
      width: '40%',
      validators: [Validators.required],
    },
    {
      name: 'condutorValidade',
      label: 'Validade da habilitação',
      type: 'date',
      width: '40%',
      mask: '0*',
      validators: [Validators.required],
    },
    {
      name: 'condutorTelefone',
      label: 'Telefone',
      type: 'telefone',
      width: '60%',
      validators: [Validators.required],
    },
    {
      name: 'condutorCep',
      label: 'CEP',
      type: 'text',
      width: '30%',
      mask: '00000-000',
      validators: [Validators.required],
      onChange: this.buscarCepCondutor.bind(this),
    },
    {
      name: 'condutorLogradouro',
      label: 'Logradouro',
      type: 'text',
      width: '50%',
      validators: [Validators.required],
    },
    {
      name: 'condutorNumero',
      label: 'Número',
      type: 'text',
      width: '20%',
      mask: '0000',
      validators: [Validators.required],
    },
    {
      name: 'condutorBairro',
      label: 'Bairro',
      type: 'text',
      width: '60%',
      validators: [Validators.required],
    },
    {
      name: 'condutorMunicipioId',
      label: 'Município',
      type: 'search',
      width: '40%',
      searchOptions: {
        key: 'descricao',
        primaryKey: 'id',
        format: (value: string) => value + '%',
        filter: this.municipioRepository.filter.bind(this.municipioRepository),
      },
    },
    {
      name: '',
      label: 'Dados do terceiro',
      type: 'separator',
    },
    {
      name: 'terceiros[0].nome',
      label: 'Nome completo',
      type: 'text',
      width: '60%',
    },
    {
      name: 'terceiros[0].cpf',
      label: 'CPF',
      type: 'text',
      mask: '000.000.000-00',
      width: '40%',
    },
    {
      name: 'terceiros[0].veiculoPlaca',
      label: 'Placa do veículo',
      type: 'text',
      mask: 'AAAAAAA',
      width: '40%',
    },
    {
      name: 'terceiros[0].veiculoModelo',
      label: 'Modelo do veículo',
      type: 'text',
      width: '60%',
    },
    {
      name: '',
      label: 'Dados do local do acidente',
      type: 'separator',
    },
    {
      name: 'localCep',
      label: 'CEP',
      type: 'text',
      width: '30%',
      mask: '00000-000',
      onChange: this.buscarCepLocal.bind(this),
    },
    {
      name: 'localLogradouro',
      label: 'Logradouro',
      type: 'text',
      width: '70%',
      validators: [Validators.required],
    },
    {
      name: 'localNumero',
      label: 'Número',
      type: 'text',
      mask: '0000',
      width: '30%',
      validators: [Validators.required],
    },
    {
      name: 'localBairro',
      label: 'Bairro',
      type: 'text',
      width: '60%',
      validators: [Validators.required],
    },
    {
      name: 'localMunicipioId',
      label: 'Município',
      type: 'search',
      width: '40%',
      searchOptions: {
        key: 'descricao',
        primaryKey: 'id',
        format: (value: string) => value + '%',
        filter: this.municipioRepository.filter.bind(this.municipioRepository),
      },
      validators: [Validators.required],
    },
  ];

  protected camposDadosCliente: Campo[] = [
    {
      name: 'cpfCondutor',
      label: 'CPF',
      type: 'text',
      mask: '000.000.000-00',
      width: '50%',
      onChange: (value: any) => this.getCondutorByCPF(value),
    },
    {
      name: 'nomeCondutor',
      label: 'Nome Completo',
      type: 'text',
      width: '50%',
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'emailCondutor',
      label: 'E-mail',
      type: 'text',
      width: '50%',
      validators: [Validators.required, Validators.email],
    },
    {
      name: 'telefone',
      label: 'Telefone',
      type: 'telefone',
      width: '50%',
      validators: [Validators.required],
    },
    {
      name: 'municipioId',
      label: 'Município',
      type: 'search',
      width: '50%',
      searchOptions: {
        key: 'descricao',
        format: (value: string) => value + '%',
        filter: this.municipioRepository.filter.bind(this.municipioRepository),
      },
    },
    {
      name: 'localizacao',
      label: 'Localização',
      type: 'text',
      width: '50%',
    },
    {
      name: 'descricaoOcorrido',
      label: 'Descrição do ocorrido',
      type: 'textarea',
      width: '100%',
      validators: [Validators.required],
    },
  ];

  protected get isFormValid() {
    return (
      this.formVeiculo?.isValid &&
      this.formCliente?.isValid &&
      (this.formSinistro?.isValid || this.formVeiculo.get('registrarSinistro') !== 'S')
    );
  }

  constructor(
    private dialogRef: MatDialogRef<AbrirAtendimentoComponent>,
    private changeDetector: ChangeDetectorRef,
    private contratoRepository: ContratoRepository,
    private contratoGrupoRepository: ContratoGrupoRepository,
    private anexoRepository: AnexoRepository,
    private atendimentoRepository: AtendimentoRepository,
    private fornecedoresRepository: FornecedoresRepository,
    private motivoAtendimentoRepository: MotivoAtendimentoRepository,
    private motivoSinistroRepository: MotivoSinistroRepository,
    private municipioRepository: MunicipioRepository,
    private authService: AuthService,
    private condutorRepository: CondutorRepository,
    private cepRepository: CepRepository,
  ) {}

  /** Para evitar erros em ambiente de desenvolvimento */
  public ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  getCondutorByCPF(cpf: string) {
    if (cpf?.length < 11) return;
    
    this.condutorRepository.getByCPF(cpf).subscribe((result) => {
      const data = result?.data;

      if (!data.id) {
        this.condutorId = undefined;
        return;
      }

      this.condutorId = data.id;

      this.formCliente?.patchValue({ 
        nomeCondutor: this.formCliente?.form.get('nomeCondutor')?.value || data.pessoa.razaoSocial,
        telefone: this.formCliente?.form.get('telefone')?.value || data.pessoa.telefone,
        emailCondutor: this.formCliente?.form.get('emailCondutor')?.value || data.pessoa.email,
      });
    });
  }

  protected buscarCepCondutor(cep: string) {
    const cleanCep = (cep || '').replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    this.cepRepository.buscarPorCep(cleanCep).subscribe({
      next: (endereco) => {
        if (!endereco || endereco.erro) return;

        this.formSinistro?.form.patchValue({
          condutorLogradouro: endereco.logradouro || '',
          condutorBairro: endereco.bairro || '',
        });

        if (endereco.localidade) {
          const queryBusca = endereco.ibge ? `codigoIBGE=${endereco.ibge}` : `descricao=${endereco.localidade}%`;
          this.municipioRepository.filter(queryBusca).subscribe({
            next: (municipios) => {
              if (municipios?.length > 0) {
                const municipioEncontrado = municipios.find(m =>
                  endereco.uf ? m.label.toLowerCase().includes(endereco.uf.toLowerCase()) : true
                ) || municipios[0];

                if (municipioEncontrado) {
                  this.formSinistro?.form.patchValue({
                    condutorMunicipioId: municipioEncontrado.value,
                  });
                }
              }
            }
          });
        }
      },
      error: (err) => {
        console.error('Erro ao buscar CEP:', err);
      }
    });
  }

  protected buscarCepLocal(cep: string) {
    const cleanCep = (cep || '').replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    this.cepRepository.buscarPorCep(cleanCep).subscribe({
      next: (endereco: any) => {
        if (!endereco || endereco.erro) return;

        this.formSinistro?.form.patchValue({
          localLogradouro: endereco.logradouro || '',
          localBairro: endereco.bairro || '',
        });

        if (endereco.localidade) {
          const queryBusca = endereco.ibge ? `codigoIBGE=${endereco.ibge}` : `descricao=${endereco.localidade}%`;
          this.municipioRepository.filter(queryBusca).subscribe({
            next: (municipios: any[]) => {
              if (municipios?.length > 0) {
                const municipioEncontrado = municipios.find((m: any) =>
                  endereco.uf ? m.label.toLowerCase().includes(endereco.uf.toLowerCase()) : true
                ) || municipios[0];

                if (municipioEncontrado) {
                  this.formSinistro?.form.patchValue({
                    localMunicipioId: municipioEncontrado.value,
                  });
                }
              }
            }
          });
        }
      },
      error: (err: any) => {
        console.error('Erro ao buscar CEP:', err);
      }
    });
  }

  protected onSubmit(veiculoData: any, sinistroData: any, clienteData: any, anexos: FileData[]) {
    if (sinistroData) {
      const hasTerceiros = sinistroData.terceiros?.[0]?.nome;

      if (!hasTerceiros && sinistroData.terceiros) {
        delete sinistroData.terceiros;
      }
      
      const [horas, minutos] = (sinistroData.horaAcidente || '00:00').split(':');
      const dataHora = moment.utc(sinistroData.dataAcidente)
        .set({ hour: parseInt(horas), minute: parseInt(minutos), second: 0, millisecond: 0 })
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
      sinistroData.dataHora = dataHora;

      // Remove os campos separados que o backend não espera
      delete sinistroData.dataAcidente;
      delete sinistroData.horaAcidente;

      // Adiciona a placa do veículo selecionado ao sinistro
      const veiculoSelecionado = this.veiculosDisponiveis.find((v: any) => v.value === veiculoData.veiculoId);
      if (veiculoSelecionado) {
        sinistroData.placa = veiculoSelecionado.label;
      }
    }

    const body = { ...veiculoData, ...clienteData, celularCondutor: clienteData.telefone, sinistro: sinistroData };

    if (this.condutorId) {
      body.condutorId = this.condutorId;
    }
    
    this.atendimentoRepository.postRequest(body).subscribe({
      next: ({ data }: any) => {
        if (anexos.length > 0) {
          this.anexoRepository.postAnexos('AtendimentosCliente', data.id, anexos).subscribe({
            next: () => {
              this.dialogRef.close(true);
            },
            error: () => {
              modal.fire({
                icon: 'error',
                title: 'Erro ao salvar anexos',
                html: 'Tente novamente mais tarde por meio dos detalhes do atendimento',
              });

              this.dialogRef.close(false);
            },
          });
        } else {
          this.dialogRef.close(true);
        }
      },
      error: (error) => {
        modal.fire({
          icon: error.icon,
          title: error.title,
          html: error.message,
        });
      },
    });
  }

  protected onClose() {
    this.dialogRef.close(false);
  }
}
